import { message } from "antd";
import { Room, RoomEvent } from "livekit-client";
import { useCallback, useEffect, useMemo } from "react";
import { getLivekitConnectionDetails } from "V2.0/services/livekit";

export default function useLivekitWebCall(workspaceId, assistantId) {
  const room = useMemo(() => new Room(), []);

  useEffect(() => {
    const onMediaDevicesError = (error: Error) => {
      message.error(`${error.name}: ${error.message}`);
    };

    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);

    return () => {
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
    };
  }, [room]);

  const fetchLivekitConnection = async (workspaceId, assistantId) => {
    try {
      const response = await getLivekitConnectionDetails({
        workspaceId,
        assistantId,
      });

      if (response.status === 200) {
        return response.data;
      }
      throw new Error("Failed to get web call connection details");
    } catch (error) {
      throw error;
    }
  };

  const onConnect = useCallback(
    async (onClose) => {
      try {
        const livekitConnection = await fetchLivekitConnection(
          workspaceId,
          assistantId
        );
        await room.connect(livekitConnection.url, livekitConnection.token);
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (error) {
        if (error?.name && error?.reasonName) {
          message.error(`${error.name}: ${error.reasonName}`);
        } else {
          console.log({ error });
          message.error("Failed to make a web call");
        }
        onClose();
      }
    },
    [room, workspaceId, assistantId]
  );

  return {
    room,
    onConnect,
    fetchLivekitConnection,
  };
}
